import * as Colors from '@brightlayer-ui/colors';
import { InfoListItem } from '@brightlayer-ui/react-components';
import { PrivacyTip } from '@mui/icons-material';
import { Box, Theme, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { CopyButton } from '../../../components/CopyButton';
import { FilledTextField } from '../../../components/FilledTextField';
import { GenericModal } from '../../../components/GenericModal';
import { useAlert } from '../../../lib/AlertContext';
import { OrganizationSecretWithValue } from '../../../services/organization/types';
import { OrganizationId } from '../../../types/idFlavors';
import { formatDate } from '../../../utils';

export const styles = {
    secretsContainer: (theme: Theme): object => ({
        backgroundColor: Colors.yellow[100],
        paddingTop: '16px',
        paddingBottom: '40px',
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        maxwidth: '100%',
    }),
    securityInfoBar: {
        marginBottom: '16px',
        backgroundColor: Colors.yellow[50],
        paddingTop: '12px',
        paddingBottom: '12px',
    },
    expirationTextContainer: {
        marginBottom: '4px',
    },
    copyBtnStyle: { textTransform: 'uppercase', borderRadius: '5px', width: '100%' },
    titleStyle: { paddingTop: '4px', paddingBottom: '4px', textTransform: 'capitalize' },
    textInputStyle: { width: '100%', marginBottom: '0px', marginRight: '0px' },
};

type GenerateSecurityTokenConfirmationModalProps = {
    organizationSecret: OrganizationSecretWithValue[];
    organizationName: string;
    organizationId: OrganizationId;
    setShowConfirmationModal: (show: boolean) => void;
};

export const GenerateSecurityTokenConfirmationModal: React.FC<GenerateSecurityTokenConfirmationModalProps> = (
    props
) => {
    const { organizationSecret, setShowConfirmationModal } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const { showConfirmationToUser, clearAlert } = useAlert();
    const showSaveSecurityTokenAlert = (): void => {
        showConfirmationToUser({
            title: t('CREATE_ORGANIZATION_PAGE.CONFIRM_BEFORE_EXIT_TITLE'),
            description: t('CREATE_ORGANIZATION_PAGE.CONFIRM_BEFORE_EXIT_DESCRIPTION'),
            confirmText: t('COMMON.OK'),
            cancelText: t('COMMON.CANCEL'),
            onConfirm: () => {},
            onCancel: () => {
                clearAlert();
                setShowConfirmationModal(true);
            },
        });
        setShowConfirmationModal(false);
    };
    return (
        <>
            <GenericModal
                modalTestId="regenerate-success-modal"
                title={t('CREATE_ORGANIZATION_SUCCESS_PAGE.YOUR_SECRETS')}
                confirmText={t('COMMON.DONE')}
                confirmOnClick={(): void => {
                    showSaveSecurityTokenAlert();
                }}
            >
                <Box sx={styles.secretsContainer(theme)} data-testid="copy-secrets-success-modal">
                    <InfoListItem
                        data-testid="credentials-warning"
                        title={t('ORGANIZATION_DETAILS_PAGE.COPY_INSTRUCTIONS')}
                        wrapSubtitle={true}
                        subtitle={t('ORGANIZATION_DETAILS_PAGE.COPY_WARNING')}
                        divider={'full'}
                        wrapTitle={true}
                        wrapInfo={true}
                        statusColor={Colors.yellow[500]}
                        subtitleSeparator={'/'}
                        icon={<PrivacyTip />}
                        sx={styles.securityInfoBar}
                        disabled={undefined}
                        selected={undefined}
                    />
                    <Typography sx={styles.expirationTextContainer} variant="body2">
                        <i data-testid="regenerate-secret-expiration">
                            {t('ORGANIZATION_DETAILS_PAGE.NEW_SECRET_EXPIRATION_MESSAGE', {
                                expiration: formatDate(DateTime.fromISO(organizationSecret[0].expiration)),
                            })}
                        </i>
                    </Typography>
                    <FilledTextField
                        sx={styles.textInputStyle}
                        data-testid="new-secret-text"
                        label={`${t('ORGANIZATION_DETAILS_PAGE.SECRET')} 1`}
                        value={organizationSecret[0].secret}
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <CopyButton content={organizationSecret[0].secret} copyBtnStyle={styles.copyBtnStyle} />
                            ),
                        }}
                    />

                    <Typography sx={styles.expirationTextContainer} variant="body2">
                        <i data-testid="regenerate-secret-expiration">
                            {t('ORGANIZATION_DETAILS_PAGE.NEW_SECRET_EXPIRATION_MESSAGE', {
                                expiration: formatDate(DateTime.fromISO(organizationSecret[1].expiration)),
                            })}
                        </i>
                    </Typography>
                    <FilledTextField
                        sx={styles.textInputStyle}
                        data-testid="new-secret-text"
                        label={`${t('ORGANIZATION_DETAILS_PAGE.SECRET')} 2`}
                        value={organizationSecret[1].secret}
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <CopyButton content={organizationSecret[1].secret} copyBtnStyle={styles.copyBtnStyle} />
                            ),
                        }}
                    />
                </Box>
            </GenericModal>
        </>
    );
};
